import moment from 'moment'
import { ISO3166Countries, ISO3166States, dateFromXID } from 'services/utils'
import { FETCHED_BILLING_INFO, FLAGS_CHANGED, NEW_LICENSES_AVAILABLE } from 'services/StudioEvents';
import { States } from '../../app.router';

import BillingInformationController from './information-modal/controller';
import BillingInformationTemplate from './information-modal/template.html';

import SubscriptionController from './subscription-modal/controller';
import SubscriptionTemplate from './subscription-modal/template.html';

import PlanModalController from './plan-modal/controller'
import PlanModalTemplate from './plan-modal/template.html'

import AddLicensesController from '../../common/add-licenses-modal/controller';
import AddLicensesTemplate from '../../common/add-licenses-modal/template.html';

/* @ngInject */
export default class BillingController {
    
    constructor(ProjectManager, Loader, $state, IAMService, DialogService, $http, FeatureFlags, AnalyticsService, StorageService, $rootScope, ToastService, SensorService, $filter) {
        this.ProjectManager = ProjectManager;
        this.$state = $state;
        this.IAMService = IAMService;
        this.DialogService = DialogService;
        this.$http = $http;
        this.Loader = Loader;
        this.FeatureFlags = FeatureFlags;
        this.AnalyticsService = AnalyticsService;
        this.StorageService = StorageService;
        this.$rootScope = $rootScope;
        this.ToastService = ToastService
        this.SensorService = SensorService
        this.$filter = $filter

        this.onSubscriptionReorder = this.onSubscriptionReorder.bind(this)
        this.onInvoiceReorder = this.onInvoiceReorder.bind(this)
    }

    get invoicesEmptyState() {
        return !this.Loader.isShowLoader() && this.invoices.length === 0;
    }

    get subscriptionsEmptyState() {
        return !this.Loader.isShowLoader() && this.subscriptions.length === 0;
    }

    get hasInvoiceEmail() {
        return Boolean(this.billingInfo.invoiceContact?.email)
    }

    get hasSubscriptionContact() {
        return Boolean(this.billingInfo.subscriptionContact?.email) && Boolean(this.billingInfo.subscriptionContact?.firstName) && Boolean(this.billingInfo.subscriptionContact?.lastName)
    }

    get hasBillingAddress() {
        return Boolean(this.billingAddress)
    }

    get missingBillingInfo() {
        return !this.hasInvoiceEmail || !this.hasSubscriptionContact || !this.hasBillingAddress;
    }

    get hasWriteAccess() {
        return Boolean(this.billingInfo.accessLevel?.writeAccess)
    }

    get hasReadAccess() {
        return Boolean(this.billingInfo.accessLevel?.readAccess)
    }

    get unclaimedSensors() {
        if (this.salesOrders.length > 0) {
            return this.salesOrders.map(salesOrder => salesOrder.unclaimedSensorCount).reduce((a, b) => a + b)
        }
        return 0
    }

    get unclaimedCloudConnectors() {
        if (this.salesOrders.length > 0) {
            return this.salesOrders.map(salesOrder => salesOrder.unclaimedCloudConnectorCount).reduce((a, b) => a + b)
        }
        return 0
    }

    get billingAddress() {
        const address = this.billingInfo.billingAddress

        function hasValue(string) {
            return string.length > 0
        }

        // Check if any required values are missing
        if (Object.keys(address).length > 0 && 
            hasValue(address.companyName) && hasValue(address.country) && 
            hasValue(address.addr1) && hasValue(address.zip) && hasValue(address.city)) {
            const country = ISO3166Countries()[address.country]
            /* eslint-disable prefer-template */ 
            return `${address.companyName}, 
                    ${address.addr1} ${address.addr2}, 
                    ${address.zip} ${address.city}${address.country === "US" ? (' ' + ISO3166States()[address.state]) : ''},
                    ${country}`;
            /* eslint-enable prefer-template */ 
        }
        return ""
    }

    get hasNewBillingModel() {
        return this.FeatureFlags.isActive('billing_new_billing_model')
    }

    get formattedRenewalDate() {
        return moment(this.billingPlan.nextRenewalDate).format('MMMM Do, YYYY')
    }

    get subscriptionLicenses() {
        return (this.billingPlan && Array.isArray(this.billingPlan.licenses)) ? this.billingPlan.licenses : []
    }

    get hasSubscriptionLicenses() {
        return this.subscriptionLicenses.length > 0
    }

    get formattedStudioPlan() {
        return this.billingPlan.studioPlan.charAt(0) + this.billingPlan.studioPlan.slice(1).toLowerCase()
    }

    get formattedBillingInterval() {
        if (!this.loadedBillingPlan) {
            return ''
        }

        const interval = this.billingPlan.billingInterval

        // Handle unexpected types
        if (typeof interval !== 'string' || !interval) {
            return ''
        }

        // Map known billing intervals to human-readable format
        // These values are used in the template as "Next {interval} Invoice"
        const intervalMap = {
            'QUARTERLY': 'Quarterly',
            'YEARLY': 'Yearly',
            'EVERY_2_YEARS': '2-Year',
            'EVERY_3_YEARS': '3-Year',
            'EVERY_4_YEARS': '4-Year',
            'EVERY_5_YEARS': '5-Year'
        }

        if (intervalMap[interval]) {
            return intervalMap[interval]
        }

        // Fallback: format unknown string values (e.g. 'MONTHLY' -> 'Monthly')
        return interval.charAt(0).toUpperCase() + interval.slice(1).toLowerCase().replace(/_/g, ' ')
    }

    get formattedSensorPrice() {
        if (!this.hasPriceLevel) {
            return '-';
        }
        return this.formatCurrency(this.billingPlan.currency, this.billingPlan.centsPerSensorPerYear / 100)
    }

    get formattedCCONPrice() {
        if (!this.hasPriceLevel) {
            return '-';
        }
        return this.formatCurrency(this.billingPlan.currency, this.billingPlan.centsPerCconPerYear / 100)
    }

    get nextInvoiceAmount() {
        if (!this.hasPriceLevel) {
            return '-';
        }

        // Actual mode: NetSuite calculated the exact amount
        if (this.billingPlan.nextInvoiceAmountCents != null) {
            return this.formatCurrency(this.billingPlan.currency, this.billingPlan.nextInvoiceAmountCents / 100)
        }

        // New customer mode: no renewal date means no active subscriptions, no meaningful number to estimate
        if (!this.hasRenewalDate) {
            return '-'
        }

        // Estimate mode (failure fallback): client-side calculation from license counts × annual price
        const cost = (this.billingPlan.sensorLicensesForRenewal * this.billingPlan.centsPerSensorPerYear / 100)
            + (this.billingPlan.cconLicensesForRenewal * this.billingPlan.centsPerCconPerYear / 100)
        return this.formatCurrency(this.billingPlan.currency, cost)
    }

    get hasActualInvoiceAmount() {
        return this.billingPlan && this.billingPlan.nextInvoiceAmountCents != null
    }

    get hasRenewalDate() {
        return this.billingPlan && this.billingPlan.nextRenewalDate != null
    }

    $onInit() {

        this.orgName = this.ProjectManager.currentProject.organizationDisplayName;
        this.stateTitle = `${this.orgName} | Billing`;

        this.invoiceOrder = ['-issueDate','-invoiceNumber'];
        this.subscriptionOrder = ['nextRenewalTime']
        
        this.overdueInvoice = false

        this.activeTab = this.hasNewBillingMode ? 'INVOICES' : 'SUBSCRIPTIONS'
        this.expandedSalesOrderRows = []
        this.expandedKits = []
        this.invoices = [];
        this.salesOrders = []
        this.downloadingInvoices = []; // List of invoices waiting to be downloaded
        this.subscriptions = [];
        
        this.billingInfoAvailable = false;
        this.loadedBillingInfo = false;
        this.loadedCustomerStatus = false;
        this.billingInfo = {}

        // Initialize hasPriceLevel to false by default
        this.hasPriceLevel = false;

        this.Loader.promises = [this.loadBillingInformation(), this.loadInvoices(), this.loadSubscriptions(), this.loadSalesOrders(), this.loadCustomerStatus()]

        const orgId = this.ProjectManager.currentProject.organization.split("/")[1]
        if (orgId) {
            this.orgCreatedDate = dateFromXID(orgId)
        }
        
        this.loadedBillingPlan = false

        this.licensesInUse = {
            sensors: 0,
            cellular: 0
        }
        
        // Feature flag might have been loaded before this controller, so check if it's active
        if (this.hasNewBillingModel) {
            this.loadBillingPlan()
            this.activeTab = 'INVOICES'
        }

        // If Studio is reloaded on this page, it won't know the feature flag state yet, so listen for a callback on load
        this.$rootScope.$on(FLAGS_CHANGED, () => {
            if (this.hasNewBillingModel) {
                // If the new billing model is enabled, default to the INVOICES tab
                if (this.activeTab === 'SUBSCRIPTIONS') {
                    this.activeTab = 'INVOICES'
                }
                this.loadBillingPlan()
            }
        })

        this.$rootScope.$on(NEW_LICENSES_AVAILABLE, () => {
            this.loadBillingPlan()
        })

        this.showProjectCostBreakdown = false
        this.projectCostBreakdownOrder = '-activeSensors'
        this.projectsCostBreakdown = []
        this.projectSearchQuery = ''
        this.filteredProjectsCostBreakdown = this.projectsCostBreakdown
        this.loadingProjectsCostBreakdown = false
        this.projectsCostBreakdownLoaded = false
    }

    loadBillingPlan() {
        this.IAMService.getBillingPlan().then(plan => {
            this.loadedBillingPlan = true
            this.billingPlan = plan
        }).catch(error => {
            const errorMessage = error.data ? error.data.error : 'Could not load the billing plan, please try again later.'
            this.ToastService.showSimpleText(errorMessage)
        })

        this.SensorService.licensesInUse().then(licensesInUse => {
            this.licensesInUse = licensesInUse
        }).catch(error => {
            const errorMessage = error.data ? error.data.error : 'Could not load the licenses in use, please try again later.'
            this.ToastService.showSimpleText(errorMessage)
        })
    }

    loadCustomerStatus() {
        return this.IAMService.getCustomerStatus().then(customerStatus => {
            this.hasPriceLevel = customerStatus.hasPriceLevel === true; // Only show prices if explicitly true
            
            // Customer on the SaaS plan will have location based subscriptions set to true
            // They should not be shown any of the details in the the subscription section
            this.locationBasedSubscriptions = customerStatus.locationBasedSubscriptions === true; // Only set location based subscriptions if explicitly true
        }).catch(error => {
            console.error('Could not load customer status:', error); // eslint-disable-line no-console
            this.hasPriceLevel = false; // Default to false on error
            this.locationBasedSubscriptions = false; // Default to false on error
        }).finally(() => {
            this.loadedCustomerStatus = true
        })
    }

    showAddLicensesModal() {
        this.DialogService.show({
            controller: AddLicensesController,
            controllerAs: '$ctrl',
            template: AddLicensesTemplate,
            parent: document.body,
            clickOutsideToClose: true,
            escapeToClose: true,
            fullscreen: true,
            locals: {
                billingPlan: this.billingPlan,
                licensesInUse: this.licensesInUse,
                hasPriceLevel: this.hasPriceLevel,
                reloadBillingPlan: () => {
                    this.loadBillingPlan()
                }
            }
        });
    }

    calculateProjectCost(project) {
        if (!this.hasPriceLevel) {
            return {
                "monthly": '-',
                "yearly": '-'
            }
        }
        // Return an object with monthly and yearly costs
        const yearlySensorCost = project.activeSensors * this.billingPlan.centsPerSensorPerYear / 100
        const yearlyCConCost = project.activeCcons * this.billingPlan.centsPerCconPerYear / 100
        return {
            "monthly": this.formatCurrency(this.billingPlan.currency, (yearlySensorCost + yearlyCConCost) / 12),
            "yearly": this.formatCurrency(this.billingPlan.currency, yearlySensorCost + yearlyCConCost)
        }
    }

    toggleProjectCostBreakdown() {
        this.showProjectCostBreakdown = !this.showProjectCostBreakdown
        
        // Load projects if opening and not already loaded
        if (this.showProjectCostBreakdown && !this.projectsCostBreakdownLoaded && !this.loadingProjectsCostBreakdown) {
            this.loadProjectsCostBreakdown()
        }
    }

    loadProjectsCostBreakdown() {
        this.loadingProjectsCostBreakdown = true
        const organization = this.ProjectManager.currentProject.organization
        
        this.getProjectsPage(null, [], organization)
            .then(projects => {
                this.projectsCostBreakdown = this.processProjectsForCostBreakdown(projects)
                this.filteredProjectsCostBreakdown = this.projectsCostBreakdown
                this.projectsCostBreakdownLoaded = true
            })
            .catch(() => {
                this.ToastService.showSimpleText('Could not load projects, please try again later.')
            })
            .finally(() => {
                this.loadingProjectsCostBreakdown = false
                this.$rootScope.$applyAsync()
            })
    }

    getProjectsPage(pageToken, previousData, organization) {
        const requestParams = {
            organization,
            pageToken
        }
        return this.IAMService
            .projects(requestParams)
            .then(({ data, nextPageToken }) => {
                const allData = [...previousData, ...data];
                if (nextPageToken !== '') {
                    return this.getProjectsPage(nextPageToken, allData, organization);
                }
                return allData;
            });
    }

    processProjectsForCostBreakdown(projects) {
        return projects.map(project => {
            const deviceCounts = project.deviceCounts || {}
            
            // Calculate active sensors (all device types except 'ccon')
            let activeSensors = 0
            let activeCcons = 0
            
            Object.entries(deviceCounts).forEach(([deviceType, counts]) => {
                const subscriptionsUsed = counts.subscriptionsUsed || 0
                if (deviceType === 'ccon') {
                    activeCcons = subscriptionsUsed
                } else {
                    activeSensors += subscriptionsUsed
                }
            })
            
            return {
                name: project.name,
                displayName: project.displayName,
                activeSensors,
                activeCcons
            }
        })
    }

    getProjectYearlyCost(project) {
        if (!this.hasPriceLevel || !this.billingPlan) {
            return 0
        }
        const yearlySensorCost = project.activeSensors * this.billingPlan.centsPerSensorPerYear / 100
        const yearlyCConCost = project.activeCcons * this.billingPlan.centsPerCconPerYear / 100
        return yearlySensorCost + yearlyCConCost
    }

    getSortedProjectsCostBreakdown() {
        const projects = this.filteredProjectsCostBreakdown
        const order = this.projectCostBreakdownOrder
        
        if (!order || projects.length === 0) {
            return projects
        }

        
        // Extract the field name by removing the '-' prefix if present (e.g., '-monthlyCost' becomes 'monthlyCost')
        const orderField = order.startsWith('-') ? order.substring(1) : order
        // Determine sort direction: true if order starts with '-' (descending), false otherwise (ascending)
        const isDescending = order.startsWith('-')
        
        // Handle sorting for calculated cost columns
        if (orderField === 'monthlyCost' || orderField === 'yearlyCost') {
            return [...projects].sort((a, b) => {
                const costA = this.getProjectYearlyCost(a)
                const costB = this.getProjectYearlyCost(b)
                return isDescending ? costB - costA : costA - costB
            })
        }
        
        // For other fields, use Angular's orderBy filter
        return this.$filter('orderBy')(projects, order)
    }

    exportProjectsCostBreakdown() {
        if (this.filteredProjectsCostBreakdown.length === 0) {
            return
        }

        const csvData = this.filteredProjectsCostBreakdown.map(project => {
            const costs = this.calculateProjectCost(project)
            return {
                'Project Name': project.displayName,
                'Active Sensors': project.activeSensors,
                'Active Cellular Cloud Connectors': project.activeCcons,
                'Estimated Annual Cost': costs.yearly
            }
        })

        const headers = ['Project Name', 'Active Sensors', 'Active Cellular Cloud Connectors', 'Estimated Annual Cost']
        const csvContent = this.convertToCSV(csvData, headers)
        this.downloadCSV(csvContent, `project-cost-breakdown-${new Date().toISOString().split('T')[0]}.csv`)
    }

    convertToCSV(data, headers) {
        if (data.length === 0) {
            return `${headers.join(',')}\n`
        }
        
        const headerLine = headers.join(',')
        const rows = data.map(row => 
            headers.map(header => {
                const value = row[header]
                // Escape quotes and wrap in quotes if contains comma or quote
                const stringValue = String(value)
                if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                    return `"${stringValue.replace(/"/g, '""')}"`
                }
                return stringValue
            }).join(',')
        )
        
        return [headerLine, ...rows].join('\n')
    }

    downloadCSV(content, filename) {
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', filename)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        }
    }

    openProjectInNewTab(project) {
        // Extract projectId from project.name (format: "projects/projectId")
        const projectId = project.name.split('/')[1]
        if (projectId) {
            const projectUrl = this.$state.href(States.SENSORS, { projectId }, { absolute: true })
            window.open(projectUrl, '_blank')
        }
    }

    searchProjects({ query }) {
        this.projectSearchQuery = query
        if (this.projectSearchQuery === '') {
            this.filteredProjectsCostBreakdown = this.projectsCostBreakdown
        } else {
            this.filteredProjectsCostBreakdown = this.projectsCostBreakdown.filter(project => project.displayName.toLowerCase().includes(this.projectSearchQuery.toLowerCase()))
        }
    }

    clearProjectSearchResults() {
        this.projectSearchQuery = ''
        this.filteredProjectsCostBreakdown = this.projectsCostBreakdown
    }

    getClaimedDeviceCountForSalesOrder(salesOrder) {
        if (!salesOrder) {
            return 0
        }
        return salesOrder.sensorCount + salesOrder.cloudConnectorCount - salesOrder.unclaimedSensorCount - salesOrder.unclaimedCloudConnectorCount
    }

    getDeviceCountForSalesOrder(salesOrder) {
        if (!salesOrder) {
            return 0
        }
        return salesOrder.sensorCount + salesOrder.cloudConnectorCount
    }

    getUnclaimedDevicesCountForKit(kit) {
        if (!kit || !kit.devices) {
            return 0
        }
        return kit.devices.filter(device => !device.isClaimed).length
    }

    expandSalesOrder(salesOrder, index) {

        if (this.expandedSalesOrderRows.includes(index)) {
            this.expandedSalesOrderRows.splice(this.expandedSalesOrderRows.indexOf(index), 1);
        } else {
            this.expandedSalesOrderRows.push(index);
        }
        
        if (!salesOrder.kits) {
            this.IAMService.getKitsFromSalesOrder(salesOrder.salesOrderId).then(data => {
                // Sort the kits based on the number of unclaimed devices in descending order
                data.kits.sort((a, b) => {
                    const unclaimedA = a.devices.filter(device => !device.isClaimed).length;
                    const unclaimedB = b.devices.filter(device => !device.isClaimed).length;
                    return unclaimedB - unclaimedA; // For descending order
                })
                salesOrder.kits = data.kits
                this.$rootScope.$applyAsync()
            })
        }
    }

    formatCurrency(currency, amount) { // eslint-disable-line class-methods-use-this
        const formatter = new Intl.NumberFormat(undefined, { // Uses user locale for formatting when set to 'undefined'
            style: 'currency',
            currency
        })
        return formatter.format(amount)
    }

    daysLeft(date) { // eslint-disable-line class-methods-use-this
        return moment(date).diff(moment(), 'days')
    }

    subscriptionPeriod(period) { // eslint-disable-line class-methods-use-this
        switch (period) {
            case 'YEARS_1':
                return '1 Year'
            case 'YEARS_2':
                return '2 Years'
            case 'YEARS_3':
                return '3 Years'
            case 'YEARS_4':
                return '4 Years'
            case 'YEARS_5':
                return '5 Years'
            default:
                return "Unspecified"
        }
    }

    licenseNextPayment(license) {
        // `nextPayment` is the date of the line's next invoice. For an off-cycle
        // purchase this is the prorated invoice aligning it to the billing date.
        return this.formatLicenseDate(license && license.nextPayment)
    }

    licenseRenewalDate(license) {
        // `renewalDate` is the date the line's current term ends and it renews.
        return this.formatLicenseDate(license && license.renewalDate)
    }

    licensePaymentPeriod(license) { // eslint-disable-line class-methods-use-this
        // Display-only formatting: the numbers come preformatted from NetSuite.
        // A prorated payment covers the days aligning the line to the billing
        // date; a regular one covers the customer's billing period label.
        if (!license) {
            return '-'
        }
        if (license.prorated && license.proratedDays != null) {
            return `${license.proratedDays} days (prorated)`
        }
        return license.paymentPeriod || '-'
    }

    formatLicenseDate(date) { // eslint-disable-line class-methods-use-this
        if (!date) {
            return '-'
        }
        // Parse only the YYYY-MM-DD substring (per Kim) so the displayed date isn't
        // shifted by a day via timezone conversion if the backend sends a full ISO
        // datetime. Parsing with an explicit format treats it as local, no TZ offset.
        return moment(String(date).substring(0, 10), 'YYYY-MM-DD').format('MMM D, YYYY')
    }

    onCostBreakdownReorder(order) {
        if (order === '') {
            this.projectCostBreakdownOrder = ['-activeSensors', '-activeCcons']
        }
    }

    onInvoiceReorder(invoiceOrder) { 
        if (invoiceOrder === '') {
            this.order = ['-issueDate','-invoiceNumber'];
        }
        this.AnalyticsService.trackEvent(`billing.invoice.sort.${invoiceOrder}`)
    }
    
    onSubscriptionReorder(subscriptionOrder) {
        if (subscriptionOrder === '') {
            this.order = 'nextRenewalTime'
        }

        this.AnalyticsService.trackEvent(`billing.subscription.sort.${subscriptionOrder}`)
    }

    loadBillingInformation() {
        return this.IAMService.getBillingInformation().then(response => {
            this.billingInfo = response.billingInfo
            this.billingInfoAvailable = true
            this.loadedBillingInfo = true
            this.updateBillingBadgeCheck()
        }).catch(() => {
            this.billingInfoAvailable = false
            this.loadedBillingInfo = true
        })
    }
    
    loadInvoices() {
        return this.IAMService.listInvoices().then(invoices => {
            this.invoices = invoices.data.filter(invoice => invoice.amount !== "0");
            if (this.invoices.length > 0) {
                this.updateBillingBadgeCheck()
            }
        })
    }

    loadSalesOrders() {
        this.loadingSalesOrders = true
        return this.IAMService.getSalesOrders().then(data => {
            this.salesOrders = data.salesOrders
        }).finally(() => {
            this.loadingSalesOrders = false
        })
    }

    loadSubscriptions() {
        return this.IAMService.listSubscriptions().then(subscriptions => {
            this.subscriptions = subscriptions.data
        })
    }

    updateBillingBadgeCheck() {
        if (this.loadedBillingInfo) {
            this.$rootScope.$broadcast(FETCHED_BILLING_INFO)
        }
    }
    
    showBillingInformationModal(ev) {

        this.DialogService.show({
            class: 'billing-information-modal',
            controller: BillingInformationController,
            controllerAs: '$ctrl',
            template: BillingInformationTemplate,
            parent: document.body,
            targetEvent: ev,
            clickOutsideToClose: true,
            escapeToClose: true,
            fullscreen: true,
            locals: {
                billingInfo: this.billingInfo
            }
        });
    }

    showSubscriptionModal(ev, subscription) {

        this.DialogService.show({
            controller: SubscriptionController,
            controllerAs: '$ctrl',
            template: SubscriptionTemplate,
            parent: document.body,
            targetEvent: ev,
            clickOutsideToClose: true,
            escapeToClose: true,
            fullscreen: true,
            locals: {
                subscription
            }
        });
    }

    showPlanModal(ev) {
        this.DialogService.show({
            controller: PlanModalController,
            controllerAs: '$ctrl',
            template: PlanModalTemplate,
            parent: document.body,
            targetEvent: ev,
            clickOutsideToClose: true,
            escapeToClose: true,
            fullscreen: false,
            locals: {
                billingPlan: this.billingPlan
            }
        });
    }

    downloadPDF(invoiceNumber) {
        this.downloadingInvoices.push(invoiceNumber)
        this.IAMService.generateInvoicePDF(invoiceNumber).then(response => {
            
            // Create a link element, hide it, direct it towards the blob, and then 'click' it programatically
            const a = document.createElement("a");
            a.style = "display: none";
            document.body.appendChild(a);
            // Create a DOMString representing the blob and point the link element towards it
            const url = window.URL.createObjectURL(response.data);
            a.href = url;
            a.download = `${invoiceNumber}.pdf`;
            a.click(); // Programatically click the link to trigger the download
            // Release the reference to the file by revoking the Object URL
            window.URL.revokeObjectURL(url);
            this.downloadingInvoices.splice(this.downloadingInvoices.indexOf(invoiceNumber), 1);
        })

        this.AnalyticsService.trackEvent("billing.invoice.download")
    }
}
